/**
 * Analytics abstraction layer — provider-agnostic event dispatch.
 *
 * Events are categorised into tiers:
 * - funnel: step markers for conversion measurement (PostHog + GA4)
 * - why: explains drop-off reasons (PostHog + GA4)
 * - operational: system health / error diagnosis (PostHog only)
 *
 * GA4 receives funnel + why events. Six key funnel events are mapped to
 * GA4 recommended e-commerce event names for free dashboards and Ads
 * integration.
 */

// ---------------------------------------------------------------------------
// Provider interface
// ---------------------------------------------------------------------------

interface AnalyticsProvider {
  name: string;
  capture: (event: string, properties: Record<string, unknown>) => void;
  identify?: (userId: string, traits?: Record<string, unknown>) => void;
  reset?: () => void;
}

const providers: AnalyticsProvider[] = [];

export function registerProvider(provider: AnalyticsProvider): void {
  providers.push(provider);
}

export function capture(event: string, properties: Record<string, unknown>): void {
  for (const provider of providers) {
    try {
      provider.capture(event, properties);
    } catch {
      // Analytics must never break the app
    }
  }
}

export function identify(userId: string, traits?: Record<string, unknown>): void {
  for (const provider of providers) {
    try {
      provider.identify?.(userId, traits);
    } catch {
      // Analytics must never break the app
    }
  }
}

export function reset(): void {
  for (const provider of providers) {
    try {
      provider.reset?.();
    } catch {
      // Analytics must never break the app
    }
  }
}

// ---------------------------------------------------------------------------
// Event tiers
// ---------------------------------------------------------------------------

type EventTier = 'funnel' | 'why' | 'operational';

const EVENT_TIERS: Record<string, EventTier> = {
  // Registration funnel
  rego_started: 'funnel',
  rego_auth_completed: 'funnel',
  rego_step_viewed: 'funnel',
  rego_checkout_initiated: 'funnel',
  rego_payment_completed: 'funnel',
  rego_completed: 'funnel',
  // Donation funnel
  donation_modal_opened: 'funnel',
  donation_amount_selected: 'funnel',
  donation_details_submitted: 'funnel',
  donation_checkout_initiated: 'funnel',
  donation_payment_completed: 'funnel',
  donation_completed: 'funnel',
  // Why — registration
  rego_email_checked: 'why',
  rego_login_fallback: 'why',
  rego_resume_from_existing: 'why',
  rego_payment_retry: 'why',
  rego_settlement_resolved: 'why',
  rego_voucher_presented: 'why',
  rego_redirect_resumed: 'why',
  rego_redirect_failed: 'why',
  rego_awaiting_settlement: 'why',
  rego_settlement_keep_waiting: 'why',
  rego_session_recovery: 'why',
  // Why — donation
  donation_modal_closed: 'why',
  donation_redirect_resumed: 'why',
  donation_redirect_failed: 'why',
  donation_session_recovery: 'why',
  donation_payment_failed: 'why',
  donation_validation_shown: 'why',
  // Shipping
  rego_shipping_submitted: 'funnel',
  // Operational
  rego_error: 'operational',
  rego_settlement_timeout: 'operational',
  donation_error: 'operational',
  donation_step_viewed: 'operational',
  // Checkout-page diagnostics (PostHog-only; see checkout-analytics.ts)
  checkout_page_viewed: 'operational',
  checkout_intent_created: 'operational',
  checkout_intent_failed: 'operational',
  checkout_stripe_js_failed: 'operational',
  checkout_element_ready: 'operational',
  checkout_element_load_error: 'operational',
  checkout_element_timeout: 'operational',
  checkout_pay_clicked: 'operational',
  checkout_payment_error: 'operational',
  checkout_express_ready: 'operational',
  checkout_express_cancelled: 'operational',
  // Cloudflare asked this visitor to tick the box (lib/turnstile.ts)
  turnstile_interactive: 'operational',
};

// ---------------------------------------------------------------------------
// GA4 e-commerce event mapping
// ---------------------------------------------------------------------------

interface GTMMapping {
  ga4Event: string | ((props: Record<string, unknown>) => string | null);
  mapPayload: (props: Record<string, unknown>) => Record<string, unknown>;
}

function stripUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      result[key] = value;
    }
  }
  return result;
}

function makeItem(name: string, category: string, price?: unknown): Record<string, unknown> {
  return stripUndefined({
    item_name: name,
    item_category: category,
    price: typeof price === 'number' ? price : undefined,
    quantity: 1,
  });
}

const GTM_EVENT_MAP: Record<string, GTMMapping> = {
  rego_started: {
    ga4Event: 'begin_checkout',
    mapPayload: (props) => ({
      ecommerce: stripUndefined({
        currency: props.currency,
        value: 0,
        items: [makeItem(String(props.ride_name ?? 'registration'), 'registration')],
      }),
    }),
  },
  rego_checkout_initiated: {
    ga4Event: 'add_payment_info',
    mapPayload: (props) => ({
      ecommerce: stripUndefined({
        currency: props.currency,
        value: props.donation_amount,
        payment_type: props.payment_type,
        items: [makeItem('Registration Donation', 'registration', props.donation_amount)],
      }),
    }),
  },
  rego_payment_completed: {
    ga4Event: 'purchase',
    mapPayload: (props) => ({
      ecommerce: stripUndefined({
        transaction_id: props.donation_id,
        value: props.amount,
        currency: props.currency,
        payment_type: props.payment_method,
        items: [makeItem('Registration Donation', 'registration', props.amount)],
      }),
    }),
  },
  donation_modal_opened: {
    ga4Event: 'view_item',
    mapPayload: (props) => ({
      ecommerce: {
        items: [
          makeItem(String(props.recipient_name ?? 'donation'), String(props.scope ?? 'donation')),
        ],
      },
    }),
  },
  donation_checkout_initiated: {
    ga4Event: 'begin_checkout',
    mapPayload: (props) => ({
      ecommerce: stripUndefined({
        currency: props.currency,
        value: props.total_charge ?? props.amount,
        items: [
          makeItem(
            'Donation',
            String(props.scope ?? 'donation'),
            props.total_charge ?? props.amount,
          ),
        ],
      }),
    }),
  },
  donation_payment_completed: {
    ga4Event: 'purchase',
    mapPayload: (props) => ({
      ecommerce: stripUndefined({
        transaction_id: props.donation_id,
        value: props.amount,
        currency: props.currency,
        payment_type: props.payment_method,
        items: [makeItem('Donation', String(props.scope ?? 'donation'), props.amount)],
      }),
    }),
  },
  donation_amount_selected: {
    ga4Event: 'add_to_cart',
    mapPayload: (props) => ({
      ecommerce: stripUndefined({
        currency: props.currency,
        value: props.amount,
        items: [makeItem('Donation', String(props.scope ?? 'donation'), props.amount)],
      }),
    }),
  },
  rego_auth_completed: {
    ga4Event: (props) => (props.auth_mode === 'login' ? 'login' : 'sign_up'),
    mapPayload: (props) => ({ method: props.auth_mode }),
  },
  rego_completed: {
    ga4Event: (props) => (props.donation_id ? null : 'generate_lead'),
    mapPayload: (props) => stripUndefined({ currency: props.currency, value: 0 }),
  },
  rego_shipping_submitted: {
    ga4Event: 'add_shipping_info',
    mapPayload: (props) => ({
      ecommerce: stripUndefined({
        currency: props.currency,
        shipping_tier: props.country,
        items: [makeItem('Registration', 'registration')],
      }),
    }),
  },
};

// ---------------------------------------------------------------------------
// PostHog provider
// ---------------------------------------------------------------------------

function createPostHogProvider(): AnalyticsProvider {
  return {
    name: 'posthog',
    capture: (event, properties) => {
      // Dynamic import avoids hard dependency — PostHog may not be initialised
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const posthog = require('posthog-js').default;
      if (typeof posthog?.capture === 'function') {
        posthog.capture(event, properties);
      }
    },
    identify: (userId) => {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const posthog = require('posthog-js').default;
      if (typeof posthog?.identify === 'function') {
        posthog.identify(userId);
      }
    },
    reset: () => {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const posthog = require('posthog-js').default;
      if (typeof posthog?.reset === 'function') {
        posthog.reset();
      }
    },
  };
}

// ---------------------------------------------------------------------------
// GTM / dataLayer provider
// ---------------------------------------------------------------------------

function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(payload);
}

function createGTMProvider(): AnalyticsProvider {
  return {
    name: 'gtm',
    capture: (event, properties) => {
      if (typeof window === 'undefined') return;

      const tier = EVENT_TIERS[event];
      // Operational events stay in PostHog only
      if (tier === 'operational') return;

      const mapping = GTM_EVENT_MAP[event];
      if (mapping) {
        const ga4Event =
          typeof mapping.ga4Event === 'function' ? mapping.ga4Event(properties) : mapping.ga4Event;
        // Dynamic mappings can return null to skip (e.g. rego_completed with payment)
        if (!ga4Event) return;
        // Clear previous ecommerce data to prevent contamination
        pushToDataLayer({ ecommerce: null });
        pushToDataLayer({
          event: ga4Event,
          ...mapping.mapPayload(properties),
        });
      } else if (tier === 'funnel' || tier === 'why') {
        // Forward as custom event with original name
        pushToDataLayer({ event, ...stripUndefined(properties) });
      }
    },
    identify: (userId) => {
      pushToDataLayer({ user_id: userId });
    },
  };
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;

  registerProvider(createPostHogProvider());
  registerProvider(createGTMProvider());
}

// ---------------------------------------------------------------------------
// Session replay control
// ---------------------------------------------------------------------------
//
// Recording is globally off (disable_session_recording in
// instrumentation-client). These start/stop the PostHog recorder on demand —
// used to capture a specific flow (donation / registration → checkout) rather
// than every session. `startSessionRecording(true)` overrides sampling so the
// capture is deterministic. Requires "Record user sessions" enabled in the
// PostHog project settings to ingest.

export function startSessionReplay(): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const posthog = require('posthog-js').default;
    if (typeof posthog?.startSessionRecording === 'function') {
      posthog.startSessionRecording(true);
    }
  } catch {
    // Recording must never break the flow.
  }
}

export function stopSessionReplay(): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const posthog = require('posthog-js').default;
    if (typeof posthog?.stopSessionRecording === 'function') {
      posthog.stopSessionRecording();
    }
  } catch {
    // Recording must never break the flow.
  }
}
